/**
 * MediaLightbox — click-to-zoom image gallery with keyboard nav.
 *
 * Usage:
 *   <MediaLightbox
 *     images={[{ image_url, caption }, ...]}
 *     openIndex={0}
 *     onClose={() => setOpen(null)}
 *   />
 *
 * Renders nothing when `openIndex` is null. Keyboard: Esc closes,
 * arrows advance. Backdrop click closes.
 */
import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function MediaLightbox({ images, openIndex, onClose }) {
  const [idx, setIdx] = useState(openIndex ?? 0);

  useEffect(() => { setIdx(openIndex ?? 0); }, [openIndex]);

  useEffect(() => {
    if (openIndex == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") setIdx((i) => Math.min(i + 1, images.length - 1));
      else if (e.key === "ArrowLeft")  setIdx((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    // Freeze body scroll while open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [openIndex, images?.length, onClose]);

  if (openIndex == null || !images || images.length === 0) return null;
  const cur = images[idx] || images[0];
  const canPrev = idx > 0;
  const canNext = idx < images.length - 1;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="media-lightbox-backdrop"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
        aria-label="Close"
        data-testid="media-lightbox-close"
      >
        <X className="w-6 h-6" />
      </button>

      {canPrev && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setIdx(idx - 1); }}
          className="absolute left-4 md:left-8 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20"
          aria-label="Previous"
          data-testid="media-lightbox-prev"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}
      {canNext && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setIdx(idx + 1); }}
          className="absolute right-4 md:right-8 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20"
          aria-label="Next"
          data-testid="media-lightbox-next"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      )}

      <figure
        className="max-w-6xl w-full max-h-full flex flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
        data-testid="media-lightbox-figure"
      >
        <img
          src={cur.image_url}
          alt={cur.caption || ""}
          className="max-w-full max-h-[80vh] object-contain rounded"
        />
        {(cur.caption || images.length > 1) && (
          <figcaption className="text-white/85 text-sm text-center">
            {cur.caption}
            {images.length > 1 && (
              <span className="ml-2 opacity-60">
                · {idx + 1} / {images.length}
              </span>
            )}
          </figcaption>
        )}
      </figure>
    </div>
  );
}
